import React from 'react';
import { Box, Paper, Typography, Skeleton } from '@mui/material';
import { Chess } from 'chess.js';

const TopMovesPanel = ({ topMoves, onMoveClick, currentFen, isMobile = false, isAnalyzing = true }) => {
    const uciToSan = (uciMove, fen) => {
        if (!fen || !uciMove) return uciMove || '';

        try {
            const chess = new Chess(fen);
            const from = uciMove.slice(0, 2);
            const to = uciMove.slice(2, 4);
            const promotion = uciMove.length > 4 ? uciMove.slice(4) : undefined;

            const move = chess.move({ from, to, promotion });
            return move ? move.san : uciMove;
        } catch (error) {
            // Silently return UCI move if conversion fails
            return uciMove;
        }
    };

    const formatFullLine = (pvLine, startFen) => {
        if (!pvLine || pvLine.length === 0 || !startFen) {
            return '';
        }

        try {
            const chess = new Chess(startFen);
            let result = '';

            const movesToShow = pvLine.slice(0, isMobile ? 6 : 15);

            for (let i = 0; i < movesToShow.length; i++) {
                const uciMove = movesToShow[i];

                // Validate UCI move format
                if (!uciMove || uciMove.length < 4) {
                    break;
                }

                const from = uciMove.slice(0, 2);
                const to = uciMove.slice(2, 4);
                const promotion = uciMove.length > 4 ? uciMove.slice(4) : undefined;

                // Validate square format
                if (!/^[a-h][1-8]$/.test(from) || !/^[a-h][1-8]$/.test(to)) {
                    break;
                }

                const moveNumber = chess.moveNumber();
                const isWhite = chess.turn() === 'w';

                if (isWhite) {
                    result += (i > 0 ? ' ' : '') + `${moveNumber}. `;
                } else if (i === 0) {
                    result += `${moveNumber}... `;
                } else {
                    result += ' ';
                }

                try {
                    const move = chess.move({ from, to, promotion });
                    if (!move) {
                        // Move is illegal in current position, stop processing
                        break;
                    }
                    result += move.san;
                } catch (moveError) {
                    // Move failed, stop processing this line
                    break;
                }
            }

            return result;
        } catch (error) {
            // Silently fail - don't spam console
            return '';
        }
    };

    const formatEvaluation = (evaluation) => {
        if (!evaluation) {
            return { text: '...', color: '#757575' };
        }

        if (evaluation.type === 'mate') {
            const mateValue = evaluation.value;
            return {
                text: mateValue > 0 ? `+M${Math.abs(mateValue)}` : `-M${Math.abs(mateValue)}`,
                color: mateValue > 0 ? '#4caf50' : '#f44336'
            };
        }

        const value = evaluation.value;
        const displayValue = Math.abs(value).toFixed(2);

        return {
            text: value > 0 ? `+${displayValue}` : value < 0 ? `-${displayValue}` : '0.00',
            color: Math.abs(value) < 0.3 ? '#757575' : value > 0 ? '#4caf50' : '#f44336'
        };
    };

    // Common box style for consistent height
    const rowStyle = {
        mb: 0.5,
        p: isMobile ? 0.5 : 0.75,
        height: isMobile ? '32px' : '38px',
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? 0.5 : 1,
    };

    // Filter out invalid moves (moves without proper data)
    const validMoves = (topMoves || []).filter(moveInfo =>
        moveInfo &&
        moveInfo.move &&
        moveInfo.evaluation
    );

    // Show placeholder only if no valid moves AND we're still waiting for first results
    // Once we have moves, keep showing them even after analysis completes
    const showPlaceholder = validMoves.length === 0;

    // Number of placeholder rows to show
    const placeholderCount = isMobile ? 2 : 3;

    return (
        <Paper elevation={2} sx={{ p: isMobile ? 0.5 : 0.75, width: '100%' }}>
            {showPlaceholder ? (
                // Render skeleton loaders with exact same dimensions
                <>
                    {Array.from({ length: placeholderCount }).map((_, index) => (
                        <Box
                            key={index}
                            sx={{
                                ...rowStyle,
                                mb: index < placeholderCount - 1 ? 0.5 : 0,
                                bgcolor: 'background.default',
                                border: '1px solid',
                                borderColor: 'divider',
                            }}
                        >
                            <Skeleton
                                variant="text"
                                width={isMobile ? 14 : 18}
                                height={16}
                                sx={{ flexShrink: 0 }}
                            />
                            <Skeleton
                                variant="rectangular"
                                width={isMobile ? 45 : 55}
                                height={isMobile ? 20 : 24}
                                sx={{ borderRadius: 0.5, flexShrink: 0 }}
                            />
                            <Skeleton
                                variant="text"
                                sx={{ flex: 1 }}
                                height={16}
                            />
                            {!isMobile && (
                                <Skeleton
                                    variant="text"
                                    width={35}
                                    height={16}
                                    sx={{ flexShrink: 0 }}
                                />
                            )}
                        </Box>
                    ))}
                </>
            ) : (
                validMoves.map((moveInfo, index) => {
                    const fullLine = formatFullLine(moveInfo.evaluation?.pvLine, currentFen);
                    const evalInfo = formatEvaluation(moveInfo.evaluation);
                    const isTopMove = index === 0;

                    return (
                        <Box
                            key={`${moveInfo.move}-${index}-${moveInfo.evaluation?.depth || 0}`}
                            sx={{
                                ...rowStyle,
                                mb: index < validMoves.length - 1 ? 0.5 : 0,
                                bgcolor: isTopMove ? 'primary.light' : 'background.default',
                                cursor: 'pointer',
                                border: isTopMove ? '1.5px solid' : '1px solid',
                                borderColor: isTopMove ? 'primary.main' : 'divider',
                                '&:hover': {
                                    bgcolor: isTopMove ? 'primary.main' : 'action.hover',
                                    boxShadow: 1
                                },
                                transition: 'background-color 0.15s, box-shadow 0.15s'
                            }}
                            onClick={() => onMoveClick(moveInfo.move)}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: 'bold',
                                    minWidth: isMobile ? 14 : 18,
                                    fontSize: isMobile ? '0.6rem' : '0.7rem',
                                    color: isTopMove ? 'primary.dark' : 'text.secondary',
                                    flexShrink: 0
                                }}
                            >
                                #{index + 1}
                            </Typography>

                            <Box
                                sx={{
                                    minWidth: isMobile ? 45 : 55,
                                    px: isMobile ? 0.5 : 0.75,
                                    py: 0.25,
                                    borderRadius: 0.5,
                                    bgcolor: 'background.paper',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    textAlign: 'center',
                                    flexShrink: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: isMobile ? '20px' : '24px'
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 'bold',
                                        fontSize: isMobile ? '0.65rem' : '0.75rem',
                                        color: evalInfo.color,
                                        lineHeight: 1
                                    }}
                                >
                                    {evalInfo.text}
                                </Typography>
                            </Box>

                            <Typography
                                variant="caption"
                                sx={{
                                    flex: 1,
                                    fontFamily: 'monospace',
                                    fontSize: isMobile ? '0.65rem' : '0.75rem',
                                    color: 'text.primary',
                                    fontWeight: isTopMove ? 600 : 400,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    lineHeight: 1
                                }}
                            >
                                {fullLine || uciToSan(moveInfo.move, currentFen)}
                            </Typography>

                            {!isMobile && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'text.disabled',
                                        fontSize: '0.65rem',
                                        minWidth: 35,
                                        textAlign: 'right',
                                        flexShrink: 0,
                                        lineHeight: 1
                                    }}
                                >
                                    d{moveInfo.evaluation?.depth || 0}
                                </Typography>
                            )}
                        </Box>
                    );
                })
            )}
        </Paper>
    );
};

export default TopMovesPanel;